import React, { useEffect, useState } from "react";
import { Link, useHistory, withRouter } from "react-router-dom";
import PageWrapper from "../../lib/page-wrapper";
import { classNames, If } from "react-extras";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  getCampaign,
  getCampaignStats,
  pauseCampaignItem,
  updateCampaignMeta,
} from "../../../action_managers/campaigns";
import get from "lodash/get";
import groupBy from "lodash/groupBy";
import filter from "lodash/filter";
import { Col, Grid, Row } from "react-bootstrap";
import { campaignItems } from "../../modals/campaign_schedule_podcast/mock_data";
import { StyledTable, TwoLineCell } from "../../lib/tables/styled-table";
import { AlbumArt } from "../../lib/album-art";
import MediaPlayer from "../../lib/media_player/media_player";
import Divider from "../../lib/divider";
import { getAverageCPMFromShow, getDurationInDays } from "../../../lib/campaigns";
import { formatMoney } from "../../../lib/format-money";
import { getBudgetsByCampaignUUID } from "../../../action_managers/budgets";
import { fetchPublicShowsByUUIDs } from "../../../action_managers/public_show";
import { useGetCampaignAndShows } from "../../../hooks/campaigns";
import { DaysToRespondProgress, DaysToUploadProgress } from "./days_to_respond_progress";
import { CONFIRMATION_MODAL } from "../../modals/modal_root";
import { showModal } from "../../../actions/modal";
import { validateCampaign } from "../../../lib/validate-campaign";
import ImpressionsProgress from "./impressions_progress";
import Moment from "moment";
import CampaignPageMetaData from "./campaign_page_metadata";
import {
  CampaignItemStateDeclined,
  CampaignItemStateExpired,
  CampaignItemStatePaused,
} from "../../../constants/campaigns";
import ContextMenu from "../../lib/context_menu";
import { Tab, TabList, TabPanel, Tabs } from "../../lib/tab/lite-tabs";
import NewPodcasterBar from "./new_podacster_bar";
import DownloadPodcastInfoLink, { downloadPodcastInfo } from "./download_podcast_info";
import DownloadCampaignPerformance from "./download_campaign_performance";

const useSelectCampaign = uuid => useSelector(state => state.campaigns?.campaigns?.[uuid]);

const CpmWithTotal = ({ campaignItem, campaign }) => (
  <span>
    Total: <span className="bold">{formatMoney(campaignItem.totalBudget)}</span> | CPM:{" "}
    <span className={"bold"}>
      {formatMoney(getAverageCPMFromShow(campaignItem.budget, campaign))}
    </span>
  </span>
);

const sentColumns = [
  {
    accessor: "name",
    Cell: ({ original: campaignItem }) => {
      const campaign = campaignItem.campaign;
      const status = () => {
        if (campaignItem.state !== "sent") {
          return <small>{campaignItem.state.replace(/^\w/, c => c.toUpperCase())}</small>;
        }
        return (
          <DaysToRespondProgress
            textFn={daysLeft => {
              if (daysLeft >= 1) {
                return `Has ${daysLeft} days to respond`;
              } else if (daysLeft === 1) {
                return `Has 1 day to respond`;
              }
              return "Has less than a day to respond";
            }}
            campaignItem={campaignItem}
          />
        );
      };
      return (
        <div className={"flex-row-container align-center"}>
          <div className={"flex-row-container align-center position-relative"}>
            <If
              condition={
                campaignItem.state === CampaignItemStateDeclined ||
                campaignItem.state === CampaignItemStateExpired
              }>
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  height: 87,
                  background: "white",
                  opacity: 0.8,
                  top: -8,
                  left: 0,
                }}
              />
            </If>
            <AlbumArt
              src={campaignItem.show?.imageURL}
              style={{ width: 48, marginRight: 10 }}
              imageSize={"64x64"}
            />
            <TwoLineCell
              title={campaignItem.show?.title}
              subtitle={<CpmWithTotal campaign={campaign} campaignItem={campaignItem} />}
            />
          </div>

          <div className="flex-1" />
          {status()}
        </div>
      );
    },
  },
];

const awaitingAudioColumns = [
  {
    accessor: "name",
    Cell: ({ original: campaignItem }) => {
      const campaign = campaignItem.campaign;
      return (
        <div className={"flex-row-container align-center"}>
          <div className={"flex-row-container align-center position-relative"}>
            <AlbumArt
              src={campaignItem.show?.imageURL}
              style={{ width: 48, marginRight: 10 }}
              imageSize={"64x64"}
            />
            <TwoLineCell
              title={campaignItem.show?.title}
              subtitle={<CpmWithTotal campaign={campaign} campaignItem={campaignItem} />}
            />
          </div>
          <div className="flex-1" />
          <DaysToUploadProgress
            textFn={daysLeft => {
              if (daysLeft >= 1) {
                return `Accepted, has ${daysLeft} days to upload audio`;
              } else if (daysLeft === 1) {
                return `Accepted, has 1 day to upload audio`;
              }
              return "Accepted, has less than a day to upload audio";
            }}
            campaignItem={campaignItem}
          />
        </div>
      );
    },
  },
];

const draftStates = draft => {
  if (!draft?.length) return null;
  const displayedItems = draft.slice(0, 8);
  return (
    <div className="text-center">
      <div className={"m-vxs bold"}>
        You have {draft.length} podcast{draft.length > 1 ? "s" : ""} selected.
      </div>
      <div>
        {displayedItems.map(item => {
          return (
            <AlbumArt
              key={item.uuid}
              imageSize={"64x64"}
              className={"small m-rxs"}
              src={item.show?.imageURL}
            />
          );
        })}
        <If condition={draft.length > displayedItems.length}>
          <span>and {draft.length - displayedItems.length} more.</span>
        </If>
      </div>
    </div>
  );
};

const CampaignPage = props => {
  const { campaignStatsByUUID, getCampaignStats, updateCampaignMeta } = props;

  const dispatch = useDispatch();
  const { campaignUUID } = props.match.params;
  const campaign = useSelectCampaign(campaignUUID);
  useGetCampaignAndShows(props, campaignUUID);
  const items = campaign?.items;
  const [tabIndex, setTabIndex] = useState(0);
  useEffect(() => {
    dispatch(getBudgetsByCampaignUUID(campaignUUID));
  }, [items, dispatch, campaignUUID]);

  useEffect(() => {
    getCampaignStats(campaignUUID);
  }, [campaignUUID, getCampaignStats]);

  const shows = props.publicShows;
  let campaignItems = campaign?.items ?? [];
  campaignItems = campaignItems.map(({ item: { showUUID, budgetUUID, ...rest } }) => ({
    show: shows?.[showUUID],
    budget: props.budgets[budgetUUID],
    campaign,
    ...rest,
  }));
  const history = useHistory();
  const { draft, sent, accepted, declined, running, completed, expired, paused } = {
    draft: [],
    sent: [],
    accepted: [],
    declined: [],
    running: [],
    completed: [],
    expired: [],
    paused: [],

    ...groupBy(campaignItems, "state"),
  };
  const awaiting_audio = filter(campaignItems, item => item.state === "awaiting-audio-upload");
  const acceptedPlusItems = accepted
    .concat(awaiting_audio)
    .concat(running)
    .concat(completed);

  const runningColumns = [
    {
      accessor: "name",
      Cell: ({ original: campaignItem }) => {
        const days = getDurationInDays(
          campaignItem.campaign,
          campaignItem.show,
          campaignItem?.budget?.total / 1000
        );
        const startDate = Moment.unix(campaignItem.startAt);
        const endDate = Moment.unix(campaignItem.startAt).add(days, "d");
        return (
          <div className={"flex-row-container align-center position-relative"}>
            <div className={"flex-row-container position-relative align-center"}>
              <If condition={campaignItem.state === CampaignItemStatePaused}>
                <div
                  style={{
                    position: "absolute",
                    width: "100%",
                    height: 87,
                    background: "white",
                    opacity: 0.8,
                    top: -8,
                    left: 0,
                  }}
                />
              </If>
              <AlbumArt
                src={campaignItem.show?.imageURL}
                style={{ width: 48, marginRight: 10 }}
                imageSize={"64x64"}
              />
              <TwoLineCell
                title={campaignItem.show?.title}
                subtitle={`Running  ${startDate.format("LL")} - ${endDate.format("LL")} `}
              />
              <ImpressionsProgress campaignItem={campaignItem} />
            </div>
            <div className="flex-1" />
            <If condition={!!campaignItem.mediaFileUUID}>
              <div className="bold margin-right--small margin-left--small">Preview</div>
              <div style={{ marginRight: 10, flex: 1, maxWidth: 200 }}>
                <MediaPlayer mediaFileUUID={campaignItem.mediaFileUUID} />
              </div>
            </If>
            <If condition={campaignItem.state === "running"}>
              <div className={"m-lxxs"}>
                <ContextMenu
                  pullRight={true}
                  menuItems={{
                    "Stop Ad": () => {
                      props.showPauseItemModal(() =>
                        props
                          .pauseCampaignItem(campaignItem.uuid)
                          .then(resp => resp.status === 200 && props.getCampaign(campaignUUID))
                      );
                    },
                  }}
                  noCircle={true}
                />
              </div>
            </If>
          </div>
        );
      },
    },
  ];

  const campaignItemByCategories = {
    inProgress: [...running, ...paused, ...accepted, ...completed],
    awaitingAudio: awaiting_audio,
    sent: [...sent, ...declined, ...expired],
  };
  const selectPodcastsButton = (
    <button
      onClick={() => props.history.push(`/campaigns/${campaignUUID}/select`)}
      className={classNames("btn width-100", draft.length ? "btn-secondary" : "btn-primary")}>
      Select Podcasts
    </button>
  );

  const csvDownloadCategories = [
    ...accepted,
    ...awaiting_audio,
    ...running,
    ...paused,
    ...completed,
  ];
  const renderCSVBanner = () => {
    const shouldShowDownloadBanner = csvDownloadCategories.some(
      ({ respondedAt }) => respondedAt > (campaign.csvLastExportedAt ?? 0)
    );
    if (!shouldShowDownloadBanner) {
      return null;
    }
    return (
      <NewPodcasterBar
        onClick={() => downloadPodcastInfo(campaign, csvDownloadCategories, updateCampaignMeta)}
      />
    );
  };

  const [isCompleteCampaign, missingFields] = validateCampaign(campaign);

  return (
    <PageWrapper
      topOfPage={renderCSVBanner()}
      title={get(campaign, "name")}
      backLink={"/campaigns"}
      isLoading={props.isLoading}
      EyeBrow={
        <div className={"m-bxxs"}>
          <Link to={`/campaigns/${campaignUUID}/view`} className={"bold "}>
            View Details
          </Link>
        </div>
      }
      topRightElement={() => {
        const topRightElements = [];
        if (campaign?.state === "created") {
          topRightElements.push(
            <Link
              className={"bold"}
              key={"edit-campaign-buton"}
              to={`/campaigns/${campaignUUID}/editing`}>
              Edit Campaign
            </Link>
          );
        }
        if (csvDownloadCategories.length) {
          topRightElements.push(
            <DownloadPodcastInfoLink
              campaign={campaign}
              campaignItems={csvDownloadCategories}
              updateCampaignMeta={updateCampaignMeta}
            />
          );
        }
        return topRightElements;
      }}>
      <Grid fluid>
        <CampaignPageMetaData
          acceptedPlusItems={acceptedPlusItems}
          campaignStats={campaignStatsByUUID[campaignUUID]}
        />
        <If condition={!draft.length}>
          <Row>
            <Col xs={12} md={6} mdOffset={3}>
              <p className={"text-center margin-top--large"}>
                {campaignItems.length
                  ? "Invite additional podcasts that are a good fit for your campaign."
                  : "Your campaign has been created! Now, send a request to the podcasts that you think would be a good fit for your campaign."}
              </p>
            </Col>
            <Col xs={12} md={4} mdOffset={4}>
              {selectPodcastsButton}
            </Col>
          </Row>
        </If>
        <If condition={!!draft.length}>
          <Row>
            <>{draftStates(draft)}</>
          </Row>
          <Row className={"m-tl"}>
            <Col xs={12} md={3} mdOffset={3}>
              {selectPodcastsButton}
            </Col>
            <Col xs={12} md={3}>
              <button
                className="btn btn-primary m-a0 width-100"
                onClick={() => {
                  isCompleteCampaign
                    ? history.push(`/campaigns/${campaignUUID}/schedule`)
                    : props.showConfirmationModal(
                        () => history.push(`/campaigns/${campaignUUID}/edit`),
                        missingFields
                      );
                }}>
                Schedule & Send
              </button>
            </Col>
          </Row>
        </If>
        <If condition={!!campaignItemByCategories.inProgress.length}>
          <>
            <div className="flex-row-container align-end">
              <h2 className="no-margin">Accepted</h2>
              <div className="flex-1" />
              <DownloadCampaignPerformance campaign={campaign} />
            </div>
            <Divider smallMargin />
            <StyledTable data={campaignItemByCategories.inProgress} columns={runningColumns} />
          </>
        </If>
        <If condition={!!campaignItemByCategories.awaitingAudio.length}>
          <>
            <div className="flex-row-container m-txl align-end">
              <h2 className="no-margin">Awaiting Audio</h2>
              <div className="flex-1" />
              <DownloadCampaignPerformance campaign={campaign} />
            </div>
            <Divider smallMargin />
            <StyledTable
              data={campaignItemByCategories.awaitingAudio}
              columns={awaitingAudioColumns}
            />
          </>
        </If>
        <If condition={!!campaignItemByCategories.sent.length}>
          <>
            <Tabs selectedIndex={tabIndex} onSelect={setTabIndex}>
              <div className={"flex-row-container m-txl align-center      "}>
                <h2 className={"m-a0"}>Invited</h2>
                <div className={"bold m-hxs"}>VIEW:</div>
                <TabList>
                  <Tab>All</Tab>
                  <Tab>Pending</Tab>
                  <Tab>Expired</Tab>
                  <Tab>Declined</Tab>
                </TabList>
              </div>
              <Divider smallMargin />
              <TabPanel>
                <StyledTable
                  data={campaignItemByCategories.sent}
                  columns={sentColumns}
                  campaign={campaign}
                />
              </TabPanel>
              <TabPanel>
                <StyledTable data={sent} columns={sentColumns} campaign={campaign} />
              </TabPanel>
              <TabPanel>
                <StyledTable data={expired} columns={sentColumns} campaign={campaign} />
              </TabPanel>
              <TabPanel>
                <StyledTable data={declined} columns={sentColumns} campaign={campaign} />
              </TabPanel>
            </Tabs>
          </>
        </If>
      </Grid>
    </PageWrapper>
  );
};

export default connect(
  state => ({
    campaigns: state.campaigns.campaigns,
    isLoading: state.campaigns.isLoading,
    campaignStatsByUUID: state.campaigns.campaignStatsByUUID,
    campaignItems: campaignItems,
    budgets: state.budgets,
    publicShows: state.publicShows,
  }),
  {
    showConfirmationModal: (callback, missingFields) => {
      return showModal(CONFIRMATION_MODAL, {
        modalTitle: "Campaign Incomplete",
        confirmationMessage: (
          <div>
            "You haven't finished entering your campaign details yet. You need to fill in all of the
            fields for your campaign before you can schedule and send it."
            <div className="margin-top--small">
              Missing Fields:
              <ul>
                {missingFields.map(field => (
                  <li key={field} className="bold">
                    {field}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ),
        callback,
        actionButtonName: "Edit Campaign",
      });
    },
    showPauseItemModal: callback => {
      return showModal(CONFIRMATION_MODAL, {
        modalTitle: "Are you sure?",
        confirmationMessage:
          'Ads should only be stopped if there is a serious problem. Selecting "Report a Problem" will alert our support team to reach out to you.',
        callback,
        actionButtonName: "Report a Problem",
      });
    },
    getBudgetsByCampaignUUID,
    getCampaign,
    updateCampaignMeta,
    fetchPublicShowsByUUIDs,
    getCampaignStats,
    pauseCampaignItem,
  }
)(withRouter(CampaignPage));
